import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import OrganizationManager from './OrganizationManager';
import ProductsManager from './ProductsManager';
import KnowledgeManager from './KnowledgeManager';
import MailingManager from './MailingManager';
import TagsManager from './TagsManager';
import { LayoutDashboard, Building2, Users, Package, LogOut, Menu, X, BookOpen, Mail, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('organization');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const menuItems = [
        { id: 'organization', label: 'Organización', icon: Building2 },
        { id: 'products', label: 'Productos y Planes', icon: Package },
        { id: 'knowledge', label: 'Conocimiento', icon: BookOpen },
        { id: 'mailing', label: 'Mailing', icon: Mail },
        { id: 'tags', label: 'Etiquetas', icon: Tag },
    ];

    return (
        <div className="min-h-screen bg-slate-50 flex font-sans text-slate-700">
            {/* Sidebar */}
            <aside 
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 shadow-xl`}
            >
                <div className="h-full flex flex-col">
                    {/* Logo Area */}
                    <div className="p-6 border-b border-slate-800 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <img src="/xp_dark.png" alt="X Project" className="h-6" />
                            <span className="font-bold text-lg tracking-tight">
                                Gestión Integral
                            </span>
                        </div>
                        <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-slate-400 hover:text-white">
                            <X size={24} />
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                        {menuItems.map(item => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveTab(item.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                                        activeTab === item.id 
                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' 
                                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                    }`}
                                >
                                    <Icon size={20} />
                                    <span className="font-medium">{item.label}</span>
                                </button>
                            );
                        })}
                    </nav>

                    {/* User Profile & Logout */}
                    <div className="p-4 border-t border-slate-800 bg-slate-900/50">
                        <div className="flex items-center gap-3 mb-4 px-2">
                            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 font-bold">
                                {user.displayName ? user.displayName[0] : 'A'}
                            </div>
                            <div className="overflow-hidden">
                                <p className="text-sm font-medium text-white truncate">{user.displayName || 'Administrador'}</p>
                                <p className="text-xs text-slate-500 truncate">{user.email}</p>
                            </div>
                        </div>
                        <button 
                            onClick={handleLogout}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-red-600/20 hover:text-red-400 text-slate-400 rounded-lg transition-colors text-sm font-medium"
                        >
                            <LogOut size={16} /> Cerrar Sesión
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col h-screen overflow-hidden">
                {/* Mobile Header */}
                <header className="md:hidden bg-white border-b border-slate-200 p-4 flex items-center justify-between">
                    <button onClick={() => setIsSidebarOpen(true)} className="text-slate-600">
                        <Menu size={24} />
                    </button>
                    <span className="font-bold text-slate-800">XP Admin</span>
                    <div className="w-6"></div> {/* Spacer */}
                </header>

                {/* Content Area */}
                <main className="flex-1 overflow-y-auto p-4 md:p-8">
                    <div className="max-w-6xl mx-auto">
                        {activeTab === 'organization' && <OrganizationManager />}
                        {activeTab === 'products' && <ProductsManager />}
                        {activeTab === 'knowledge' && <KnowledgeManager />}
                        {activeTab === 'mailing' && <MailingManager />}
                        {activeTab === 'tags' && <TagsManager />}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminDashboard;
